import { Copy, Mail } from "lucide-react";
import type { MatchCard, MatchInvite } from "@hub/shared";
import { Button, Pill, toast } from "@/ui";
import { inviteEmail } from "../lib/invites";
import s from "../network.module.css";

async function copy(text: string, what: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast(`${what} copied.`, "success");
  } catch {
    toast("Could not copy. Select the link and copy it yourself.", "danger");
  }
}

/**
 * What to do after inviting someone: Hub never sends mail, so each invited person gets a link to copy and an email
 * the viewer sends from their own mail app. Stays on the card until they join.
 */
export function InvitePanel({ card, invites }: { card: MatchCard; invites: MatchInvite[] }) {
  if (invites.length === 0) return null;
  const waiting = invites.filter((i) => !i.accepted);
  return (
    <div className={s.block} data-testid="invite-panel">
      <div className={s.blockLabel}>Invitations</div>
      <div className={`${s.small} ${s.muted}`} style={{ marginBottom: 8 }}>
        {waiting.length > 0
          ? "Hub does not send these for you. Send each person their link; the introduction is waiting for them when they sign up with that address."
          : "Everyone you invited has joined."}
      </div>
      <div className="u-col" style={{ gap: 10 }}>
        {invites.map((invite) => {
          const mail = inviteEmail(card, invite);
          return (
            <div key={invite.inviteId} className="u-col" style={{ gap: 6 }} data-testid={`invite-${invite.email}`}>
              <div className="u-row" style={{ gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600 }}>{invite.name?.trim() || invite.email}</span>
                {invite.name && <span className={`${s.small} ${s.muted}`}>{invite.email}</span>}
                <Pill tone={invite.accepted ? "success" : "neutral"}>{invite.accepted ? "Joined" : "Waiting"}</Pill>
              </div>
              {!invite.accepted && (
                <>
                  <input className={s.inviteLink} value={invite.link} readOnly onFocus={(e) => e.currentTarget.select()} aria-label={`Invite link for ${invite.name ?? invite.email}`} />
                  <div className="u-row" style={{ gap: 6, flexWrap: "wrap" }}>
                    <Button size="sm" variant="primary" icon={<Mail size={13} />} onClick={() => { window.location.href = mail.mailto; }}>
                      Email {invite.name?.trim().split(/\s+/)[0] || "them"}
                    </Button>
                    <Button size="sm" variant="outline" icon={<Copy size={13} />} onClick={() => copy(invite.link, "Link")}>
                      Copy link
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => copy(mail.body, "Message")}>
                      Copy message
                    </Button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
